import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, keyframes, type Theme } from "@mui/material/styles";

export type Tone = "primary" | "info" | "success" | "warning" | "error" | "secondary";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

/** Short entrance for stacked blocks; global reduced-motion rule neutralises it. */
export const enter = (index = 0) => ({
  animation: `${fadeUp} 200ms cubic-bezier(0.33, 0, 0.67, 1) ${Math.min(index, 8) * 40}ms both`,
});

/** Fluent outlined glyph. */
export function Glyph({ name, fontSize = "medium" as "small" | "medium" | "large", sx }: { name: string; fontSize?: "small" | "medium" | "large"; sx?: object }) {
  return (
    <Icon baseClassName="material-icons-outlined" fontSize={fontSize} sx={sx} aria-hidden>
      {name}
    </Icon>
  );
}

/** Rounded tinted tile that hosts an icon (Windows Settings style). */
export function IconTile({ name, tone = "primary", size = 36 }: { name: string; tone?: Tone; size?: number }) {
  return (
    <Box
      sx={(theme: Theme) => ({
        width: size,
        height: size,
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        borderRadius: 1,
        color: theme.palette[tone].main,
        bgcolor: alpha(theme.palette[tone].main, theme.palette.mode === "dark" ? 0.18 : 0.1),
      })}
    >
      <Glyph name={name} fontSize={size > 40 ? "large" : "medium"} />
    </Box>
  );
}

/** Group title above a bordered surface, like a Settings page section. */
export function Section({
  title,
  description,
  action,
  children,
  index = 0,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  index?: number;
}) {
  return (
    <Box component="section" aria-label={title} sx={{ minWidth: 0, ...enter(index) }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "flex-end" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1, px: 0.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" component="h2" fontWeight={600}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Stack>
      <Paper elevation={0} sx={{ overflow: "hidden" }}>
        {children}
      </Paper>
    </Box>
  );
}

/** One line inside a Section: icon tile, text, trailing slot, optional link with chevron. */
export function Row({
  to,
  icon,
  tone = "primary",
  primary,
  secondary,
  trailing,
  children,
}: {
  to?: string;
  icon?: string;
  tone?: Tone;
  primary: string;
  secondary?: string;
  trailing?: ReactNode;
  children?: ReactNode;
}) {
  const linkProps = to ? { component: RouterLink, to } : {};
  return (
    <Box
      {...linkProps}
      sx={(theme: Theme) => ({
        display: "block",
        px: 2,
        py: 1.25,
        color: "inherit",
        textDecoration: "none",
        transition: "background-color 120ms ease",
        "& + &": { borderTop: `1px solid ${theme.palette.divider}` },
        ...(to
          ? {
              cursor: "pointer",
              "&:hover": { bgcolor: "action.hover" },
              "&:active": { bgcolor: "action.selected" },
              "&:focus-visible": { outlineOffset: "-2px" },
            }
          : null),
      })}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {icon ? <IconTile name={icon} tone={tone} /> : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body1" fontWeight={500} sx={{ overflowWrap: "anywhere" }}>
            {primary}
          </Typography>
          {secondary ? (
            <Typography variant="body2" color="text.secondary" noWrap>
              {secondary}
            </Typography>
          ) : null}
        </Box>
        {trailing}
        {to ? <Glyph name="chevron_right" fontSize="small" sx={{ color: "text.secondary" }} /> : null}
      </Stack>
      {children}
    </Box>
  );
}

/** Overview tile: whole surface is the link. */
export function Tile({
  to,
  icon,
  tone,
  label,
  value,
  hint,
  index = 0,
}: {
  to: string;
  icon: string;
  tone: Tone;
  label: string;
  value: string | number;
  hint?: string;
  index?: number;
}) {
  return (
    <Paper
      component={RouterLink}
      to={to}
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        height: "100%",
        p: 2,
        color: "inherit",
        textDecoration: "none",
        transition: "background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
        "&:hover": { bgcolor: "action.hover", borderColor: "text.disabled" },
        "&:active": { bgcolor: "action.selected" },
        ...enter(index),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <IconTile name={icon} tone={tone} />
        <Glyph name="arrow_outward" fontSize="small" sx={{ color: "text.disabled" }} />
      </Stack>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h3" component="div" sx={{ lineHeight: 1.2 }}>
          {value}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}
